/* KS · Land & taal (11 Sep 2026) — the logic behind sections/ks-geo.liquid (read its header first).
   One run per page: detect (Shopify's /browsing_context_suggestions.json: IP country + browser languages) → decide (country /
   minor / language prompt, or nothing) → show once the page has loaded → switch through the section's own localization form,
   or remember "stay". The prompt speaks the visitor's language, not the page's.
   Remembered in localStorage per domain:
     ks_geo_country  {t: until, m: market the "stay" applies to, '*' = any}  — country + minor prompts
     ks_geo_language {t: until}                                                — language prompt
   Language of a shop suggestion: the visitor's COUNTRY's own language (Austria → German), the browser only choosing between a
   country's own languages (Belgium nl/fr, Switzerland de/fr); countries without one of the shop's languages → the browser's language, then English.
   The language-only bar follows the browser (it is about being able to read the page).
   Test: ?ks_geo=GB · ?ks_geo=BE-fr · ?ks_geo=DE-nl,en · ?ks_geo=live · ?ks_geo=reset (see the section header). */
(() => {
  'use strict';

  const KEY_C = 'ks_geo_country';
  const KEY_L = 'ks_geo_language';
  const DEF = {
    nl: { title: 'Winkel je vanuit een ander land?', text: 'Ga naar de winkel voor jouw land, met de juiste prijzen, verzending en taal.', badge: 'Aanbevolen', free: 'Gratis verzending vanaf [amount]', go: 'Doorgaan', stay: 'Nee, blijf hier', current: 'Nu:', other: 'Ander land of andere taal kiezen', country: 'Land', language: 'Taal', bar: 'Levertijd en verzendkosten voor jouw land tonen?', bar_go: 'Ja, graag', lang: 'Deze pagina is ook in het Nederlands beschikbaar.', lang_go: 'Bekijk in het Nederlands', close: 'Sluiten' },
    en: { title: 'Shopping from another country?', text: 'Switch to the shop for your country, with the right prices, delivery and language.', badge: 'Recommended', free: 'Free delivery from [amount]', go: 'Continue', stay: 'No, stay here', current: 'Currently:', other: 'Choose another country or language', country: 'Country', language: 'Language', bar: 'Show delivery times and shipping costs for your country?', bar_go: 'Yes, please', lang: 'This page is also available in English.', lang_go: 'View in English', close: 'Close' },
    de: { title: 'Kaufst du aus einem anderen Land ein?', text: 'Wechsle zum Shop für dein Land – mit den richtigen Preisen, Versand und Sprache.', badge: 'Empfohlen', free: 'Kostenloser Versand ab [amount]', go: 'Weiter', stay: 'Nein, hier bleiben', current: 'Aktuell:', other: 'Anderes Land oder andere Sprache wählen', country: 'Land', language: 'Sprache', bar: 'Lieferzeit und Versandkosten für dein Land anzeigen?', bar_go: 'Ja, gerne', lang: 'Diese Seite gibt es auch auf Deutsch.', lang_go: 'Auf Deutsch ansehen', close: 'Schließen' },
    es: { title: '¿Compras desde otro país?', text: 'Ve a la tienda de tu país, con los precios, el envío y el idioma correctos.', badge: 'Recomendado', free: 'Envío gratis a partir de [amount]', go: 'Continuar', stay: 'No, quedarme aquí', current: 'Ahora:', other: 'Elegir otro país u otro idioma', country: 'País', language: 'Idioma', bar: '¿Quieres ver los plazos y gastos de envío de tu país?', bar_go: 'Sí, gracias', lang: 'Esta página también está disponible en español.', lang_go: 'Ver en español', close: 'Cerrar' },
    da: { title: 'Handler du fra et andet land?', text: 'Gå til butikken for dit land, med de rigtige priser, levering og sprog.', badge: 'Anbefalet', free: 'Gratis levering fra [amount]', go: 'Fortsæt', stay: 'Nej, bliv her', current: 'Nu:', other: 'Vælg et andet land eller sprog', country: 'Land', language: 'Sprog', bar: 'Vil du se leveringstid og fragt for dit land?', bar_go: 'Ja tak', lang: 'Denne side findes også på dansk.', lang_go: 'Se på dansk', close: 'Luk' },
    sv: { title: 'Handlar du från ett annat land?', text: 'Gå till butiken för ditt land, med rätt priser, leverans och språk.', badge: 'Rekommenderas', free: 'Fri frakt från [amount]', go: 'Fortsätt', stay: 'Nej, stanna här', current: 'Nu:', other: 'Välj ett annat land eller språk', country: 'Land', language: 'Språk', bar: 'Vill du se leveranstid och fraktkostnad för ditt land?', bar_go: 'Ja tack', lang: 'Den här sidan finns också på svenska.', lang_go: 'Visa på svenska', close: 'Stäng' },
    no: { title: 'Handler du fra et annet land?', text: 'Gå til butikken for landet ditt, med riktige priser, levering og språk.', badge: 'Anbefalt', free: 'Gratis frakt fra [amount]', go: 'Fortsett', stay: 'Nei, bli her', current: 'Nå:', other: 'Velg et annet land eller språk', country: 'Land', language: 'Språk', bar: 'Vil du se leveringstid og fraktkostnad for landet ditt?', bar_go: 'Ja takk', lang: 'Denne siden finnes også på norsk.', lang_go: 'Se på norsk', close: 'Lukk' },
    it: { title: 'Acquisti da un altro paese?', text: 'Passa al negozio del tuo paese, con prezzi, spedizione e lingua giusti.', badge: 'Consigliato', free: 'Spedizione gratuita da [amount]', go: 'Continua', stay: 'No, resto qui', current: 'Ora:', other: "Scegli un altro paese o un'altra lingua", country: 'Paese', language: 'Lingua', bar: 'Vuoi vedere tempi e costi di spedizione per il tuo paese?', bar_go: 'Sì, grazie', lang: 'Questa pagina è disponibile anche in italiano.', lang_go: 'Vedi in italiano', close: 'Chiudi' },
    pl: { title: 'Robisz zakupy z innego kraju?', text: 'Przejdź do sklepu dla swojego kraju, z właściwymi cenami, dostawą i językiem.', badge: 'Polecane', free: 'Darmowa dostawa od [amount]', go: 'Dalej', stay: 'Nie, zostaję tutaj', current: 'Teraz:', other: 'Wybierz inny kraj lub język', country: 'Kraj', language: 'Język', bar: 'Pokazać czas i koszt dostawy dla Twojego kraju?', bar_go: 'Tak, poproszę', lang: 'Ta strona jest dostępna także po polsku.', lang_go: 'Zobacz po polsku', close: 'Zamknij' },
    fr: { title: 'Vous commandez depuis un autre pays ?', text: 'Passez à la boutique de votre pays, avec les bons prix, la livraison et la langue.', badge: 'Recommandé', free: 'Livraison gratuite dès [amount]', go: 'Continuer', stay: 'Non, rester ici', current: 'Actuellement :', other: 'Choisir un autre pays ou une autre langue', country: 'Pays', language: 'Langue', bar: 'Afficher les délais et frais de livraison pour votre pays ?', bar_go: 'Oui, merci', lang: 'Cette page existe aussi en français.', lang_go: 'Voir en français', close: 'Fermer' }
  };
  const BOT = /bot|crawl|spider|slurp|lighthouse|pagespeed|headlesschrome|inspectiontool|prerender/i;
  /* The country's own languages (of nl/en/de/fr, most spoken first). A shop suggestion is offered in the language of the visitor's
     COUNTRY; the browser only picks between a country's own languages (Belgium nl/fr, Switzerland de/fr, Luxembourg fr/de).
     Tako, 11 Sep: an Austrian visit must get German even from a Dutch browser; and a local with an English phone (common in NL, BE,
     DE) must not be pushed to English. Countries without one of our four languages fall back to the browser rule. */
  const OFFICIAL = { NL: ['nl'], BE: ['nl', 'fr', 'de'], DE: ['de'], AT: ['de'], CH: ['de', 'fr', 'it'], LI: ['de'], LU: ['fr', 'de'], FR: ['fr'], MC: ['fr'], GB: ['en'], IE: ['en'], ES: ['es'], AD: ['es'], DK: ['da'], SE: ['sv'], NO: ['no'], IT: ['it'], SM: ['it'], VA: ['it'], PL: ['pl'] };
  /* Oct 2026 (da/sv/no/it/pl): every OFFICIAL language is filtered through the target market's available (= published) languages
     in decide(), so listing a language here never sends anyone to a language that market does not have yet. Norwegian: Shopify's
     locale is "no", browsers say "nb"/"nn" (canon() maps them). */
  const canon = (l) => (l === 'nb' || l === 'nn' ? 'no' : l);
  const X_SVG = '<svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 14 14"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>';
  const GLOBE_SVG = '<svg aria-hidden="true" focusable="false" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="9.25"/><path d="M2.75 12h18.5M12 2.75c2.6 2.7 3.9 5.8 3.9 9.25S14.6 18.55 12 21.25M12 2.75C9.4 5.45 8.1 8.55 8.1 12s1.3 6.55 3.9 9.25"/></svg>';

  const store = {
    get(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} },
    del(k) { try { localStorage.removeItem(k); } catch (e) {} }
  };

  const params = (() => { try { return new URLSearchParams(location.search); } catch (e) { return new URLSearchParams(''); } })();
  const test = (params.get('ks_geo') || '').trim();
  const design = !!(window.Shopify && window.Shopify.designMode);

  /* the visitor's reading languages, two-letter, in preference order ("en-US, nl" → ["en", "nl"]) */
  const browser = (() => {
    const list = (navigator.languages && navigator.languages.length) ? navigator.languages : [navigator.language || ''];
    const out = [];
    list.forEach((l) => { const b = canon(String(l || '').slice(0, 2).toLowerCase()); if (b && !out.includes(b)) out.push(b); });
    return out;
  })();

  let ui = null;       // the open modal or bar
  let data = null;     // the section's JSON of the current page
  let root = null;
  let reading = browser; // the reading languages used for this decision (a test can simulate them: ?ks_geo=BE-fr)

  /* ------------------------------------------------------------------------------------------------------------------ helpers */
  function h(tag, attrs, kids) {
    const n = document.createElement(tag);
    Object.keys(attrs || {}).forEach((k) => {
      const v = attrs[k];
      if (v == null || v === false) return;
      if (k === 'text') n.textContent = v; else if (k === 'class') n.className = v; else n.setAttribute(k, v === true ? '' : v);
    });
    (kids || []).forEach((c) => { if (c) n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return n;
  }
  const svg = (markup, cls) => { const s = h('span', { class: cls, 'aria-hidden': 'true' }); s.innerHTML = markup; return s; };
  const flag = (src, cls) => (src ? h('img', { class: cls || 'ks-geo-flag', src: src, alt: '', width: 28, height: 21, loading: 'eager', decoding: 'async' }) : null);
  const days = () => Math.max(1, Number(data && data.cfg && data.cfg.snooze) || 30) * 864e5;
  const hasCopy = (l) => !!(l && (DEF[l] || (data.copy && data.copy[l])));
  function copyFor(lang) {
    const base = DEF[lang] || DEF.en;
    const over = (data.copy && data.copy[lang]) || {};
    const out = {};
    Object.keys(DEF.en).forEach((k) => { const v = over[k] != null ? String(over[k]).trim() : ''; out[k] = v || base[k] || DEF.en[k]; });
    return out;
  }
  /* country names in the prompt's language: the browser's own list first (same in test and live mode), then Shopify's names from
     the suggestion (already in the visitor's language), then the page-language name from Liquid */
  let regionNames = null;
  const setDisplayLang = (lang) => { try { regionNames = new Intl.DisplayNames([lang], { type: 'region' }); } catch (e) { regionNames = null; } };
  const nameOf = (iso, names) => {
    let n = null;
    try { n = regionNames && regionNames.of(iso); } catch (e) { n = null; }
    return (n && n !== iso ? n : null) || (names && names[iso]) || (data.countries[iso] && data.countries[iso].n) || iso;
  };
  const moneyLabel = (c) => (!c ? '' : (c.s && c.s !== c.c ? c.s + ' ' + c.c : c.c));
  const langLabel = (l) => (data.langs && data.langs[l]) || (l || '').toUpperCase();

  function push(action, extra) {
    const o = Object.assign({ event: 'ks_geo', ks_geo_action: action, ks_geo_current: data.current.country, ks_geo_market: data.current.market, ks_geo_page_language: data.current.language }, extra || {});
    if (test) o.ks_geo_test = test;
    try { (window.dataLayer = window.dataLayer || []).push(o); } catch (e) {}
    try { if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish('ks_geo_' + action, o); } catch (e) {}
  }

  /* where Shopify sends the visitor after the switch: this page, minus our own test/redirect markers. Shopify translates the
     handle for the target language and keeps every other parameter (utm_*, fbclid, filters) */
  function returnTo() {
    let u;
    try { u = new URL(location.href); } catch (e) { return location.pathname; }
    ['ks_geo', 'shpxid', 'country', 'currency', 'locale'].forEach((k) => u.searchParams.delete(k));
    const q = u.searchParams.toString();
    return u.pathname + (q ? '?' + q : '');
  }

  function remember(kind, value) { if (!test) store.set(kind, value); }

  function switchTo(country, lang, action, det) {
    remember(KEY_C, { t: Date.now() + days(), m: '*' });
    remember(KEY_L, { t: Date.now() + days() });
    push(action || 'switch', { ks_geo_detected: det || '', ks_geo_target: country, ks_geo_target_language: lang || '' });
    const f = root && root.querySelector('form');
    if (!f) return;
    const set = (name, value) => {
      let i = f.querySelector('input[name="' + name + '"]');
      if (!i) { i = h('input', { type: 'hidden', name: name }); f.appendChild(i); }
      i.value = value;
    };
    set('country_code', country);
    if (lang) set('language_code', lang);
    const back = returnTo();
    set('return_to', back);
    /* a switch within the same domain returns to the referring URL rather than return_to: clean the address bar first so our
       test/redirect markers (?ks_geo, ?shpxid, ?country) don't travel along */
    try { history.replaceState(history.state, '', back + location.hash); } catch (e) {}
    if (ui) ui.classList.add('is-busy');
    f.submit();
  }

  function close() {
    if (!ui) return;
    const el = ui; ui = null;
    document.documentElement.classList.remove('ks-geo-lock');
    if (el.tagName === 'DIALOG') { try { el.close(); } catch (e) {} el.remove(); return; }
    el.classList.remove('is-in');
    setTimeout(() => el.remove(), 250);
  }

  /* ------------------------------------------------------------------------------------------------------- the country modal */
  function card(t, iso, lang, names, recommended) {
    const c = data.countries[iso] || {};
    const kids = [
      flag(c.f, 'ks-geo-flag ks-geo-flag--lg'),
      h('div', { class: 'ks-geo-card__body' }, [
        h('div', { class: 'ks-geo-card__top' }, [
          h('span', { class: 'ks-geo-card__name', text: nameOf(iso, names) }),
          recommended ? h('span', { class: 'ks-geo-card__badge', text: t.badge }) : null
        ]),
        h('span', { class: 'ks-geo-card__meta', text: [moneyLabel(c), langLabel(lang)].filter(Boolean).join(' · ') }),
        c.free ? h('span', { class: 'ks-geo-card__free', text: t.free.replace('[amount]', c.free) }) : null
      ])
    ];
    return kids;
  }

  function langsFor(iso, prefer) {
    const c = data.countries[iso] || { l: [] };
    const pick = [prefer, data.current.language].concat(reading, ['en']).find((l) => l && c.l.includes(l)) || c.l[0];
    return { list: c.l, pick: pick };
  }

  function showModal(ctx) {
    close();
    const t = ctx.t;
    /* focus lands on the dialog itself (announced by its title), not on a button: no stray focus ring on touch screens */
    const d = h('dialog', { class: 'ks-geo-modal', 'aria-labelledby': 'ks-geo-title', 'aria-describedby': 'ks-geo-text', lang: ctx.copyLang, tabindex: '-1', autofocus: true });
    const x = h('button', { type: 'button', class: 'ks-geo-modal__close', 'aria-label': t.close }, [svg(X_SVG)]);
    const box = h('div', { class: 'ks-geo-card is-recommended' });
    let selCountry = ctx.det, selLang = ctx.tl;
    const paint = () => { box.replaceChildren(...card(t, selCountry, selLang, ctx.names, selCountry === ctx.det).filter(Boolean)); box.classList.toggle('is-recommended', selCountry === ctx.det); };
    paint();

    const go = h('button', { type: 'button', class: 'button button--lg w-full ks-geo-modal__go', text: t.go });
    const stay = h('button', { type: 'button', class: 'button button--lg button--outline w-full ks-geo-modal__stay', text: t.stay });

    /* "Currently:" names the shop (kunstsokken.com · € EUR · English), not the page's country: on a shared domain that country is
       Shopify's fallback for visitors it can't place (kunstsokken.com runs as Austria for a German or French visitor), which read as
       "Currently: Austria" in the live check of 11 Sep */
    const cur = data.current;
    const curC = data.countries[cur.country];
    const host = String(location.hostname || '').replace(/^www\./, '');
    const now = h('p', { class: 'ks-geo-modal__now' }, [
      h('span', { class: 'ks-geo-modal__now-label', text: t.current }),
      h('span', { text: [host, moneyLabel(curC), langLabel(cur.language)].filter(Boolean).join(' · ') })
    ]);

    /* another country or language: the same two choices as the header selectors, in one place */
    const other = h('details', { class: 'ks-geo-other' });
    const sum = h('summary', { class: 'ks-geo-other__toggle', text: t.other });
    const sc = h('select', { class: 'ks-geo-select', name: 'ks_geo_country' });
    const sl = h('select', { class: 'ks-geo-select', name: 'ks_geo_language' });
    const collator = (() => { try { return new Intl.Collator(ctx.copyLang); } catch (e) { return null; } })();
    Object.keys(data.countries)
      .sort((a, b) => (collator ? collator.compare(nameOf(a, ctx.names), nameOf(b, ctx.names)) : nameOf(a, ctx.names).localeCompare(nameOf(b, ctx.names))))
      .forEach((iso) => { const o = h('option', { value: iso, text: nameOf(iso, ctx.names) + ' (' + moneyLabel(data.countries[iso]) + ')' }); if (iso === selCountry) o.selected = true; sc.appendChild(o); });
    const fillLangs = (iso, prefer) => {
      const r = langsFor(iso, prefer);
      sl.replaceChildren(...r.list.map((l) => { const o = h('option', { value: l, text: langLabel(l) }); if (l === r.pick) o.selected = true; return o; }));
      sl.disabled = r.list.length < 2;
      selLang = r.pick;
    };
    fillLangs(selCountry, selLang);
    sc.addEventListener('change', () => { selCountry = sc.value; fillLangs(selCountry, selLang); paint(); });
    sl.addEventListener('change', () => { selLang = sl.value; paint(); });
    other.append(sum, h('div', { class: 'ks-geo-other__grid' }, [
      h('label', { class: 'ks-geo-field' }, [h('span', { class: 'ks-geo-field__label', text: t.country }), sc]),
      h('label', { class: 'ks-geo-field' }, [h('span', { class: 'ks-geo-field__label', text: t.language }), sl])
    ]));
    other.addEventListener('toggle', () => { if (other.open) push('other', { ks_geo_detected: ctx.det }); });

    if (!t.text) d.removeAttribute('aria-describedby');
    d.append(
      x,
      h('h2', { id: 'ks-geo-title', class: 'ks-geo-modal__title', text: t.title }),
      t.text ? h('p', { id: 'ks-geo-text', class: 'ks-geo-modal__text', text: t.text }) : null,
      box,
      h('div', { class: 'ks-geo-modal__actions' }, [go, stay]),
      now,
      other
    );

    const dismiss = () => { remember(KEY_C, { t: Date.now() + days(), m: data.current.market }); push('stay', { ks_geo_detected: ctx.det, ks_geo_target: ctx.det }); close(); };
    go.addEventListener('click', () => {
      if (ctx.preview) { close(); return; }
      if (selCountry === data.current.country && selLang === data.current.language) { dismiss(); return; }
      switchTo(selCountry, selLang, selCountry === ctx.det && selLang === ctx.tl ? 'switch' : 'switch_other', ctx.det);
    });
    stay.addEventListener('click', () => (ctx.preview ? close() : dismiss()));
    x.addEventListener('click', () => (ctx.preview ? close() : dismiss()));
    d.addEventListener('cancel', (e) => { e.preventDefault(); ctx.preview ? close() : dismiss(); });
    d.addEventListener('click', (e) => {
      if (e.target !== d) return;
      const r = d.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) (ctx.preview ? close() : dismiss());
    });

    document.body.appendChild(d);
    ui = d;
    document.documentElement.classList.add('ks-geo-lock');
    if (typeof d.showModal === 'function') { try { d.showModal(); } catch (e) { d.setAttribute('open', ''); } } else d.setAttribute('open', '');
    try { if (document.activeElement !== d) d.focus({ preventScroll: true }); } catch (e) {}
    if (!ctx.preview) push('shown', { ks_geo_prompt: 'country', ks_geo_detected: ctx.det, ks_geo_target: ctx.det, ks_geo_target_language: ctx.tl });
  }

  /* --------------------------------------------------------------------------------------------------------------- the bar */
  function showBar(ctx) {
    close();
    const t = ctx.t;
    const language = ctx.kind === 'language';
    const c = data.countries[ctx.det] || {};
    const text = language ? t.lang : t.bar;
    const bar = h('div', { class: 'ks-geo-bar' + (language ? ' ks-geo-bar--language' : ''), role: 'region', 'aria-live': 'polite', 'aria-label': text, lang: ctx.copyLang });
    const icon = language ? svg(GLOBE_SVG, 'ks-geo-bar__icon') : flag(c.f, 'ks-geo-flag ks-geo-flag--bar');
    const copy = h('p', { class: 'ks-geo-bar__text' }, language
      ? [h('span', { text: text })]
      : [h('strong', { class: 'ks-geo-bar__name', text: nameOf(ctx.det, ctx.names) }), h('span', { text: text })]);
    const go = h('button', { type: 'button', class: 'button button--sm ks-geo-bar__go', text: language ? t.lang_go : t.bar_go });
    const x = h('button', { type: 'button', class: 'ks-geo-bar__close', 'aria-label': t.close }, [svg(X_SVG)]);
    bar.append(icon, copy, go, x);

    const dismiss = () => {
      if (language) remember(KEY_L, { t: Date.now() + days() });
      else remember(KEY_C, { t: Date.now() + days(), m: data.current.market });
      push('stay', { ks_geo_prompt: ctx.kind, ks_geo_detected: ctx.det, ks_geo_target: ctx.det, ks_geo_target_language: ctx.tl });
      close();
    };
    go.addEventListener('click', () => (ctx.preview ? close() : switchTo(language ? data.current.country : ctx.det, ctx.tl, language ? 'switch_language' : 'switch', ctx.det)));
    x.addEventListener('click', () => (ctx.preview ? close() : dismiss()));

    document.body.appendChild(bar);
    ui = bar;
    requestAnimationFrame(() => requestAnimationFrame(() => bar.classList.add('is-in')));
    if (!ctx.preview) push('shown', { ks_geo_prompt: ctx.kind, ks_geo_detected: ctx.det, ks_geo_target: language ? data.current.country : ctx.det, ks_geo_target_language: ctx.tl });
  }

  /* ----------------------------------------------------------------------------------------------------------- the decision */
  function detect() {
    if (test && test !== 'live') {
      /* ?ks_geo=DE · ?ks_geo=DE-de (browser reads German only) · ?ks_geo=DE-nl,en (browser reads Dutch, then English) */
      const bits = test.split(/[-_:]/);
      const langs = (bits[1] || '').toLowerCase().split(',').map((s) => canon(s.trim())).filter(Boolean);
      return Promise.resolve({ country: (bits[0] || '').toUpperCase(), lang: langs[0] || browser[0] || '', names: {}, browser: langs.length ? langs : browser });
    }
    /* one lookup per 15 minutes per tab and domain: the answer (IP country + browser language) rarely changes between pages, but a
       VPN or network switch (travellers, testing) must be picked up soon, so the cached answer expires */
    const SKEY = 'ks_geo_detected';
    const TTL = 15 * 60 * 1000;
    if (test !== 'live') {
      try { const c = JSON.parse(sessionStorage.getItem(SKEY) || 'null'); if (c && typeof c.country === 'string' && c.at && Date.now() - c.at < TTL) return Promise.resolve(c); } catch (e) {}
    }
    const base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return fetch(base + 'browsing_context_suggestions.json?country%5Benabled%5D=true&language%5Benabled%5D=true', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!j) return null;
        const det = j.detected_values && j.detected_values.country && j.detected_values.country.handle;
        const parts = (j.suggestions && j.suggestions[0] && j.suggestions[0].parts) || {};
        const res = { country: det || '', lang: canon((parts.language && parts.language.handle) || ''), names: (parts.country && parts.country.options) || {}, at: Date.now() };
        try { sessionStorage.setItem(SKEY, JSON.stringify(res)); } catch (e) {}
        return res;
      })
      .catch(() => null);
  }

  /* When to ask what. `tl` = the language the visitor would get: the page's language if they read it and the target market has it,
     otherwise their best language that the market has, then English, then whatever the market offers. */
  function decide(d, opts) {
    opts = opts || {};
    if (!d || !d.country) return null;
    const cur = data.current;
    const tgt = data.countries[d.country];
    if (!tgt) return null;                                    // not sold there: nothing to suggest
    reading = d.browser || browser;
    const reads = reading.includes(cur.language);
    const inTgt = (l) => !!l && tgt.l.includes(l);
    /* the browser's pick: the page's language if the visitor reads it, else their best language this market has */
    const byBrowser = (reads && inTgt(cur.language)) ? cur.language : ([d.lang].concat(reading).find(inTgt) || (inTgt('en') ? 'en' : tgt.l[0]));
    if (tgt.m !== cur.market) {
      /* the language the suggested shop is offered in, in this order:
         1. the page's language, if it is one of the country's own and the visitor reads it (French-speaking Belgian on .nl/fr stays French);
         2. English, if the browser puts English first and the shop has it (an English-speaking expat in NL → kunstsokken.nl/en);
         3. the first of the country's own languages the browser lists (Belgium nl/fr, Switzerland de/fr);
         4. the country's main language (Austria → German, also from a Dutch browser — Tako's VPN test);
         a country without one of our four languages (Spain, Denmark …) → the browser's pick. */
      const own = (OFFICIAL[d.country] || []).filter(inTgt);
      let tl = byBrowser;
      if (own.length) {
        if (own.includes(cur.language) && reads) tl = cur.language;
        else if (reading[0] === 'en' && inTgt('en')) tl = 'en';
        else tl = reading.find((l) => own.includes(l)) || own[0];
      }
      const curC = data.countries[cur.country] || {};
      const major = tgt.c !== curC.c || tl !== cur.language;
      const style = major ? data.cfg.country : data.cfg.minor;
      if (!opts.force && (style === 'off' || !style)) return null;
      /* the prompt speaks the language of the shop it suggests */
      const copyLang = hasCopy(tl) ? tl : ((reads && hasCopy(cur.language)) ? cur.language : ([d.lang].concat(reading).find(hasCopy) || 'en'));
      return { kind: major ? 'country' : 'minor', style: style === 'off' ? 'bar' : style, det: d.country, tl: tl, copyLang: copyLang, names: d.names };
    }
    /* right market; offer the visitor's own language only when they don't read the page's (this one follows the browser: it is about
       being able to read the page, e.g. an English-speaking expat in NL, a French-speaking Swiss on .ch) */
    if (byBrowser !== cur.language && !reads && (opts.force || (data.cfg.language && data.cfg.language !== 'off'))) {
      return { kind: 'language', style: data.cfg.language === 'modal' ? 'modal' : 'bar', det: d.country, tl: byBrowser, copyLang: hasCopy(byBrowser) ? byBrowser : 'en', names: d.names };
    }
    return null;
  }

  function allowed(kind) {
    if (test) return true;
    const now = Date.now();
    if (kind === 'language') { const l = store.get(KEY_L); return !(l && l.t > now); }
    const c = store.get(KEY_C);
    return !(c && c.t > now && (c.m === '*' || c.m === data.current.market));
  }

  function present(p, preview) {
    if (!p) return;
    setDisplayLang(p.copyLang);
    const ctx = Object.assign({}, p, { t: copyFor(p.copyLang), preview: !!preview });
    if (p.kind === 'language' && p.style !== 'modal') return showBar(ctx);
    if (p.kind === 'minor' && p.style !== 'modal') return showBar(ctx);
    if (p.kind === 'country' && p.style === 'bar') return showBar(Object.assign(ctx, { kind: 'minor' }));
    if (p.kind === 'language') {
      /* language as a modal: the card shows the current country in the new language */
      return showModal(Object.assign(ctx, { det: data.current.country, t: Object.assign({}, ctx.t, { title: ctx.t.lang, text: '' }) }));
    }
    return showModal(ctx);
  }

  /* a cookie banner comes first (Shopify's native banner or Pandectes, once one is switched on — see the cookie-consent plan):
     wait until it has been answered or is gone, at most 20 s, so the two never share the screen */
  function afterConsentBanner(fn) {
    const SEL = '[id^="shopify-pc__banner"], [id^="pandectes-banner"]';
    const shown = () => Array.from(document.querySelectorAll(SEL)).some((n) => {
      const cs = getComputedStyle(n); const r = n.getBoundingClientRect();
      return cs.display !== 'none' && cs.visibility !== 'hidden' && Number(cs.opacity) > 0 && r.width > 0 && r.height > 0;
    });
    if (!shown()) { fn(); return; }
    let done = false, iv = 0;
    const go = () => { if (done) return; done = true; clearInterval(iv); fn(); };
    document.addEventListener('visitorConsentCollected', () => setTimeout(go, 400), { once: true });
    iv = setInterval(() => { if (!shown()) go(); }, 800);
    setTimeout(go, 20000);
  }

  /* the pop-up's stylesheet is loaded without blocking the page; never show the prompt before it has arrived (max 2.5 s) */
  function cssReady() {
    const link = document.querySelector('link[href*="ks-geo.css"]');
    if (!link || link.media !== 'print') return Promise.resolve();
    return new Promise((res) => { link.addEventListener('load', () => res(), { once: true }); setTimeout(res, 2500); });
  }

  /* Show once the page is usable (DOM ready + the editor's delay), not after every image, widget and pixel has finished: the
     window `load` event came 5–27 s after navigation on the preview (Trustpilot, Alia, Klaviyo, pixels), while detection itself
     takes ~0.4 s. A visitor in the wrong shop should get the question before they start reading. */
  function whenReady(fn) {
    const wait = Math.max(0, Number(data.cfg.delay) || 0);
    const go = () => setTimeout(() => cssReady().then(() => afterConsentBanner(fn)), wait);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go, { once: true }); else go();
  }

  /* ------------------------------------------------------------------------------------------------------------- the editor */
  function preview(lang) {
    const cur = data.current;
    const kind = data.cfg.preview || 'country';
    const any = Object.keys(data.countries);
    let det;
    if (kind === 'language') det = cur.country;
    else if (kind === 'minor') {
      const curC = data.countries[cur.country] || {};
      det = any.find((iso) => iso !== cur.country && data.countries[iso].m !== cur.market && data.countries[iso].c === curC.c && data.countries[iso].l.includes(cur.language)) || any.find((iso) => iso !== cur.country) || cur.country;
    } else det = cur.country === 'GB' ? (any.includes('NL') ? 'NL' : any[0]) : (any.includes('GB') ? 'GB' : any[0]);
    const c = data.countries[det] || { l: [] };
    const l = lang || (kind === 'language' ? (['en', 'fr', 'de', 'nl'].find((x) => x !== cur.language && c.l.includes(x)) || cur.language) : cur.language);
    const tl = c.l.includes(l) ? l : (c.l.includes('en') ? 'en' : c.l[0]);
    const copyLang = hasCopy(lang) ? lang : (kind === 'language' ? tl : (hasCopy(cur.language) ? cur.language : 'en'));
    present({ kind: kind, style: kind === 'country' ? (data.cfg.country || 'modal') : (data.cfg[kind] === 'modal' ? 'modal' : 'bar'), det: det, tl: tl, copyLang: copyLang, names: {} }, true);
  }

  function bindEditor() {
    const section = root.closest('.shopify-section');
    if (!section || section.dataset.ksGeoBound) return;
    section.dataset.ksGeoBound = '1';
    section.addEventListener('shopify:section:select', () => preview());
    section.addEventListener('shopify:section:deselect', close);
    section.addEventListener('shopify:block:select', (e) => { const id = e.detail && e.detail.blockId; preview((data.blocks && data.blocks[id]) || undefined); });
    section.addEventListener('shopify:block:deselect', close);
  }

  /* ------------------------------------------------------------------------------------------------------------------- start */
  function init() {
    root = document.querySelector('ks-geo[data-ks-geo]');
    if (!root) return;
    try { data = JSON.parse(root.querySelector('[data-ks-geo-data]').textContent); } catch (e) { return; }
    if (!data || !data.current || !data.countries) return;
    if (design) { bindEditor(); return; }

    if (test === 'reset') { store.del(KEY_C); store.del(KEY_L); return; }
    if (!data.cfg.on && !test) return;
    if (!test && (navigator.webdriver || BOT.test(navigator.userAgent || ''))) return;

    /* arriving from another Kunstsokken domain (auto-redirect or a selector on that domain): the country was just decided */
    if (!test && params.has('shpxid')) store.set(KEY_C, { t: Date.now() + days(), m: data.current.market });
    const linkedCountry = params.has('country');            // a link that sets the country on purpose (?country=DK)

    /* nothing could be asked on this page: skip the lookup (country answered or linked on purpose, and the visitor reads this page's
       language or already declined another) */
    const countryOpen = allowed('country') && !linkedCountry;
    const languageOpen = allowed('language') && data.cfg.language !== 'off' && !browser.includes(data.current.language);
    if (!test && !countryOpen && !languageOpen) return;
    detect().then((d) => {
      const p = decide(d);
      if (!p) return;
      if ((p.kind === 'country' || p.kind === 'minor') && (linkedCountry || !allowed('country'))) return;
      if (p.kind === 'language' && !allowed('language')) return;
      whenReady(() => { if (!ui) present(p); });
    });
  }

  /* any country/language selector on the page counts as the visitor's own choice */
  document.addEventListener('submit', (e) => {
    const f = e.target;
    if (!f || !f.matches || f.closest('ks-geo')) return;
    if (!f.matches('form.shopify-localization-form, form[action$="/localization"]')) return;
    const until = Date.now() + (data ? days() : 30 * 864e5);
    store.set(KEY_C, { t: until, m: '*' });
    store.set(KEY_L, { t: until });
  }, true);

  document.addEventListener('shopify:section:load', (e) => { if (e.target && e.target.querySelector && e.target.querySelector('ks-geo')) { close(); init(); } });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
